import React, { useEffect, useState } from "react";
import { fetchPermissibleSites, auth, db } from "../utils/firebase";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Person, Logout } from "@mui/icons-material";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import "./styleSheets/Admin.css";

function Admin() {
  const [sites, setSites] = useState([]);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState("");
  const [fadingOut, setFadingOut] = useState(false);

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    const fetchUserDataAndSites = async () => {
      try {
        const userQuery = query(
          collection(db, "userAccounts"),
          where("email", "==", currentUser.email)
        );
        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userDoc = userSnapshot.docs[0].data();
          setUserName(userDoc.name || "User");
        } else {
          console.warn("User document not found.");
        }

        const permissibleSites = await fetchPermissibleSites(currentUser.email);
        setSites(permissibleSites);
      } catch (err) {
        console.error("Error fetching user data or sites:", err);
        setError("Failed to load data. Please try again.");
      }
    };

    fetchUserDataAndSites();
  }, [currentUser, navigate]);

  const handleSiteClick = async (siteName) => {
    setLoading(true);
    try {
      const siteDocRef = doc(db, "sites", siteName);
      const siteDoc = await getDoc(siteDocRef);

      if (!siteDoc.exists()) {
        console.error(`Site document not found: ${siteName}`);
        setLoading(false);
        return;
      }
      const datesCollectionRef = collection(siteDocRef, "dates");
      const datesSnapshot = await getDocs(datesCollectionRef);

      if (datesSnapshot.empty) {
        console.error(`No date folders found for site ${siteName}`);
        alert(`No date data available for site: ${siteName}`);
        setLoading(false);
        return;
      }

      const dateFolders = [];
      datesSnapshot.forEach((doc) => {
        dateFolders.push({ id: doc.id, ...doc.data() });
      });
      dateFolders.sort((a, b) => b.id.localeCompare(a.id));

      const latestDate = dateFolders[0].id;
      navigate(`/site-dashboard/${siteName}/${latestDate}`, {
        state: { siteName, selectedDate: latestDate },
      });

    } catch (error) {
      console.error("Error fetching site data:", error);
      alert("Failed to load site data. Please try again.");
    }
    setLoading(false);
  };


  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate("/login");
    } catch (err) {
      console.error("Sign-out error:", err);
    }
  };

  return (
    <Box className="admin-container">
      <div
        className={`background-image-container ${previewImage ? (fadingOut ? "fading-out" : "visible") : ""
          }`}
        style={{
          backgroundImage: previewImage ? `url(${previewImage})` : "none",
        }}
        onTransitionEnd={() => {
          if (fadingOut) setPreviewImage("");
        }}
      ></div>


      <Typography variant="h4" className="welcome-message" gutterBottom>
        Welcome {userName || " "}
      </Typography>

      <Paper className="site-list-container">
        <Typography variant="h6" className="site-list-title" gutterBottom>
          Your Sites
        </Typography>

        <List className="site-list">
          {sites.length > 0 ? (
            sites.map((site) => (
              <ListItem
                key={site.name}
                button
                onClick={() => handleSiteClick(site.name)}
                className="site-list-item"
                onMouseEnter={() => {
                  setFadingOut(false);
                  setPreviewImage(site.thumbnailUrl || "");
                }}
                onMouseLeave={() => {
                  setFadingOut(true);
                }}
              >
                <ListItemText primary={site.name} />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" className="no-sites-message">
              No sites available
            </Typography>
          )}
        </List>
      </Paper>

      <div className="admin-button-container">
        <Button
          variant="contained"
          onClick={() => navigate("/account-info")}
          className="account-info-button"
        >
          <Person /> My Account
        </Button>

        <Button
          variant="contained"
          onClick={handleSignOut}
          className="sign-out-button"
          startIcon={<Logout />}
        >
          Sign Out
        </Button>
      </div>
    </Box>
  );
}

export default Admin;
